import { FC, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { HiCheckCircle, HiClock } from "react-icons/hi";

const steps = [
  {
    id: 1,
    actor: "Analyst",
    action: "I have a meeting with a portfolio company in 1hr, please review the latest earnings call, news and my notes. Give me a report of topics I should be diving into."
  },
  {
    id: 2,
    actor: "Quantly",
    action: "Reviews latest news, last 4 earnings calls, equity research reports and analyst notes"
  },
  {
    id: 3,
    actor: "Quantly", 
    action: "Extracts relevant themes, concerns and relevant data points"
  },
  {
    id: 4,
    actor: "Quantly",
    action: "Produces a report with main concerns, topics and questions to ask with evidence and rationale behind each one"
  }
];

interface StatsPanelProps {
  onComplete?: (isComplete: boolean) => void;
}

const StatsPanel: FC<StatsPanelProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (currentStep < steps.length && !isComplete) {
      const timer = setTimeout(() => {
        setCompletedSteps(prev => [...prev, currentStep]);
        if (currentStep === steps.length - 1) {
          setIsComplete(true);
          onComplete?.(true);
        } else {
          setCurrentStep(currentStep + 1);
        }
      }, 3000);

      return () => clearTimeout(timer);
    } else if (isComplete) {
      // Reset animation after 5 seconds of showing completion
      const resetTimer = setTimeout(() => {
        setCurrentStep(0);
        setCompletedSteps([]);
        setIsComplete(false);
        onComplete?.(false);
      }, 5000);

      return () => clearTimeout(resetTimer);
    }
  }, [currentStep, isComplete, onComplete]);

  const getStepStatus = (stepIndex: number) => {
    if (completedSteps.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'loading';
    return 'pending';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="desktop:col-span-5 laptop:col-span-5 tablet:col-span-1 mobile:col-span-1 desktop:-mt-16 laptop:-mt-16 tablet:mt-8 mobile:mt-8"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="mb-4"
      >
        <div className="inline-flex items-center gap-2 bg-blue-100 rounded-full desktop:px-4 laptop:px-4 tablet:px-3 mobile:px-3 desktop:py-2 laptop:py-2 tablet:py-1.5 mobile:py-1.5">
          <div className="desktop:w-2 laptop:w-2 tablet:w-1.5 mobile:w-1.5 desktop:h-2 laptop:h-2 tablet:h-1.5 mobile:h-1.5 bg-blue-500 rounded-full animate-pulse" />
          <span className="text-blue-700 desktop:text-sm laptop:text-sm tablet:text-xs mobile:text-xs font-medium tracking-wide">
            EXAMPLE WORKFLOW
          </span>
        </div>
      </motion.div>

      <div className="desktop:space-y-3 laptop:space-y-3 tablet:space-y-2 mobile:space-y-2">
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          
          return (
            <div
              key={step.id}
              className={`rounded-xl desktop:p-4 laptop:p-4 tablet:p-3 mobile:p-3 transition-colors duration-200 will-change-auto border-2 ${
                status === 'completed' 
                  ? 'bg-green-100 border-green-300' 
                  : status === 'loading'
                  ? 'bg-blue-100 border-blue-300'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start desktop:space-x-3 laptop:space-x-3 tablet:space-x-2 mobile:space-x-2">
                <div className={`desktop:w-8 laptop:w-8 tablet:w-7 mobile:w-6 desktop:h-8 laptop:h-8 tablet:h-7 mobile:h-6 rounded-full flex items-center justify-center transition-colors duration-200 will-change-auto ${
                  status === 'completed' 
                    ? 'bg-green-600' 
                    : status === 'loading'
                    ? 'bg-blue-600'
                    : 'bg-gray-300'
                }`}>
                  {status === 'completed' ? (
                    <HiCheckCircle className="desktop:w-5 laptop:w-5 tablet:w-4 mobile:w-3.5 desktop:h-5 laptop:h-5 tablet:h-4 mobile:h-3.5 text-white" />
                  ) : status === 'loading' ? (
                    <HiClock className="desktop:w-4 laptop:w-4 tablet:w-3.5 mobile:w-3 desktop:h-4 laptop:h-4 tablet:h-3.5 mobile:h-3 text-white animate-spin" />
                  ) : (
                    <span className="text-white desktop:text-sm laptop:text-sm tablet:text-xs mobile:text-xs font-medium">{step.id}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`desktop:text-xs laptop:text-xs tablet:text-xs mobile:text-xs font-medium mb-1 transition-colors duration-200 ${
                    status === 'completed' ? 'text-green-700' :
                    status === 'loading' ? 'text-blue-700' : 'text-gray-500'
                  }`}>
                    {step.actor}
                  </div>
                  <p className={`desktop:text-sm laptop:text-sm tablet:text-xs mobile:text-xs leading-relaxed transition-colors duration-200 ${
                    status === 'completed' ? 'text-green-800' :
                    status === 'loading' ? 'text-blue-800' : 'text-gray-600'
                  }`}>
                    {step.action}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default StatsPanel;
